/**
 * A board: one column per option of the view's single select field.
 *
 * The case the row window was made a factory for. Each stack is its own window
 * with its own scroll position, its own buffer and its own count, because that
 * is what a board is: five columns of a hundred thousand rows each, only one of
 * which anybody is scrolling.
 *
 * Read-only for now. Dragging a card from one column to another is a write to
 * the select field, and it arrives with the rest of Phase B's editing pass;
 * everything under it -- a window per stack, membership decided per window --
 * is what that pass will need and is here already.
 */
import { useEffect, useRef, useState, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { cn } from '@/shared/lib/utils'
import { rowColors } from '../decorations/colors'
import { selectColorStyle } from '../fields/select-options'
import { useFieldContext } from '../fields/ui/context'
import type { FieldContext } from '../fields/behavior/behavior'
import { RowModal } from '../row/row-modal'
import {
  useRegisteredRowWindow,
  useRowQuery,
  useRowWindow,
  useRowWindowStore,
} from '../grid/use-grid'
import type { RowSearch } from '../rows/rowQuery'
import { rowAt } from '../rows/rowWindow'
import { hiddenFields, visibleFields, type FieldPlacement } from '../views/field-order'
import { RowCard } from '../views/row-card'
import { useRowDialog } from '../views/use-row-dialog'
import { boardRowFetcher, stackKey } from './fetcher'
import { boardStacks, stackField, stackView, type BoardStack } from './stacks'
import { useHoldingStack, useStackWindows, type StackWindow } from './use-stacks'

/** How tall a card is drawn, so a column can be virtualised without measuring. */
const CARD_GAP = 8
const COVER_HEIGHT = 128
const CARD_PADDING = 12
const CARD_TITLE_HEIGHT = 24
const CARD_FIELD_HEIGHT = 40

function cardHeight(fieldCount: number, hasCover: boolean): number {
  return (
    (hasCover ? COVER_HEIGHT : 0) +
    CARD_PADDING * 2 +
    CARD_TITLE_HEIGHT +
    Math.max(0, fieldCount - 1) * CARD_FIELD_HEIGHT
  )
}

export interface BoardViewProps {
  view: ViewOf<'board'>
  fields: readonly Field[]
  fieldOptions: Record<string, FieldPlacement>
  search: RowSearch
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
}

export function BoardView({
  view,
  fields,
  fieldOptions,
  search,
  openRowId,
  onOpenRow,
}: BoardViewProps) {
  const { t } = useTranslation()
  const context = useFieldContext(fields[0]?.workspace_id)
  const { windows, publish } = useStackWindows()
  const holder = useHoldingStack(windows, openRowId)

  const field = stackField(view, fields)
  const shown = visibleFields(fields, fieldOptions)
  const coverField = fields.find((one) => one.id === view.card_cover_image_field) ?? null

  // A board with no single select field has no columns to draw. The backend
  // stores one that way -- the field is nullable and a board is created before
  // it is chosen -- so this is a state to answer for rather than an error.
  if (field === null) {
    return (
      <Empty className="p-6">
        <EmptyHeader>
          <EmptyTitle>{t('board.unconfiguredTitle')}</EmptyTitle>
          <EmptyDescription>{t('board.unconfiguredDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  const stacks = boardStacks(field)

  return (
    <>
      <div
        className="flex h-full gap-3 overflow-x-auto p-3"
        role="list"
        aria-label={t('board.label')}
      >
        {stacks.map((stack) => (
          <Stack
            key={stackKey(stack.optionId)}
            stack={stack}
            view={view}
            stackFieldId={field.id}
            fields={fields}
            shown={shown}
            coverField={coverField}
            context={context}
            search={search}
            onOpenRow={onOpenRow}
            publish={publish}
          />
        ))}
      </div>

      {openRowId !== null && holder !== null && (
        <StackRowDialog
          holder={holder}
          view={view}
          fields={fields}
          shown={shown}
          hidden={hiddenFields(fields, fieldOptions)}
          context={context}
          search={search}
          openRowId={openRowId}
          onOpenRow={onOpenRow}
        />
      )}
    </>
  )
}

/**
 * One column: its own window, its own scroll container, its own count.
 *
 * A component rather than a loop body because each one holds hooks -- a store, a
 * query, a registration -- and there is no way to call those N times from one
 * component. That is not a workaround: a stack really is an independent thing,
 * and this is React's way of saying so.
 */
function Stack({
  stack,
  view,
  stackFieldId,
  fields,
  shown,
  coverField,
  context,
  search,
  onOpenRow,
  publish,
}: {
  stack: BoardStack
  view: ViewOf<'board'>
  stackFieldId: number
  fields: readonly Field[]
  shown: readonly Field[]
  coverField: Field | null
  context: FieldContext
  search: RowSearch
  onOpenRow: (rowId: number | null) => void
  publish: (window: StackWindow) => () => void
}) {
  const { t } = useTranslation()
  const [fetcher] = useState(() => boardRowFetcher(stack.optionId))
  const store = useRowWindowStore(fetcher)

  // The view *this stack* is asking about: the board's question plus "and this
  // option". Membership is decided per window, so without it a row would be
  // reported as belonging in every column at once.
  const asked = stackView(view, stackFieldId, stack.optionId)
  useRowQuery(store, asked, search)
  const member = useRegisteredRowWindow(store, {
    tableId: view.table_id,
    viewId: view.id,
    fields,
    view: asked,
  })

  const key = stackKey(stack.optionId)
  useEffect(() => publish({ key, store, member }), [key, store, member])

  const scroller = useRef<HTMLDivElement>(null)
  const height = cardHeight(shown.length, coverField !== null)
  const count = useRowWindow(store, (state) => state.window.count)
  const answered = useRowWindow(store, (state) => state.answered)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)

  useEffect(() => {
    const element = scroller.current
    if (element === null) return
    const report = () => {
      store.getState().setViewport({
        height: element.clientHeight,
        rowHeight: height + CARD_GAP,
      })
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [store, height])

  const decorations = view.decorations ?? []
  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  return (
    <section
      role="listitem"
      aria-label={stack.option?.value ?? t('board.noOption')}
      className="bg-muted/30 flex h-full w-72 shrink-0 flex-col rounded-lg border"
    >
      <header className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        {stack.option === null ? (
          <span className="text-muted-foreground truncate text-sm">
            {t('board.noOption')}
          </span>
        ) : (
          <span
            className="truncate rounded px-2 py-0.5 text-sm"
            style={selectColorStyle(stack.option.color)}
          >
            {stack.option.value}
          </span>
        )}
        {/* The whole stack's size, which is the server's number rather than how
            many cards this column is holding. */}
        <span className="text-muted-foreground ml-auto text-xs tabular-nums">
          {answered ? count : ''}
        </span>
      </header>

      <div
        ref={scroller}
        onScroll={(event: UIEvent<HTMLDivElement>) => {
          store.getState().scrolled(event.currentTarget.scrollTop)
        }}
        data-testid={`stack-${key}`}
        className="min-h-0 flex-1 overflow-y-auto p-2"
      >
        <div className="relative" style={{ height: stackHeight(count, height) }}>
          {indexes.map((index) => {
            const row = rowAt(window, index)
            return (
              <div
                key={index}
                className="absolute inset-x-0"
                style={{ top: index * (height + CARD_GAP), height }}
              >
                {row === undefined ? (
                  <div
                    data-testid={`stack-${key}-placeholder-${index}`}
                    className={cn(
                      'bg-muted/40 h-full w-full animate-pulse rounded-lg border',
                    )}
                  />
                ) : (
                  <RowCard
                    row={row}
                    fields={shown}
                    coverField={coverField}
                    context={context}
                    colors={rowColors(decorations, row, fields, context)}
                    onOpen={() => {
                      onOpenRow(row.id)
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/** No trailing gap under the last card: the gap belongs between them. */
function stackHeight(count: number, height: number): number {
  return count === 0 ? 0 : count * (height + CARD_GAP) - CARD_GAP
}

/**
 * The dialog, rendered against the window that is holding the row.
 *
 * Its own component so that `useRowDialog`'s hooks run against one stack rather
 * than being called once per stack and thrown away -- and so that a row moving
 * from one column to another rebuilds it, which is what `key` on the holder does.
 */
function StackRowDialog({
  holder,
  view,
  fields,
  shown,
  hidden,
  context,
  search,
  openRowId,
  onOpenRow,
}: {
  holder: StackWindow
  view: ViewOf<'board'>
  fields: readonly Field[]
  shown: readonly Field[]
  hidden: readonly Field[]
  context: FieldContext
  search: RowSearch
  openRowId: number
  onOpenRow: (rowId: number | null) => void
}) {
  const window = useRowWindow(holder.store, (state) => state.window)
  const count = useRowWindow(holder.store, (state) => state.window.count)

  const openRow = useRowDialog({
    window,
    count,
    member: holder.member,
    view,
    fields,
    openRowId,
    onOpenRow,
    search,
  })

  return (
    <RowModal
      member={openRow.member}
      rowId={openRowId}
      row={openRow.row}
      missing={openRow.missing}
      fields={shown}
      hidden={hidden}
      context={context}
      steps={openRow.steps}
      onStep={openRow.onStep}
      onClose={() => {
        onOpenRow(null)
      }}
    />
  )
}
